import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import tradingQuotationService from "../services/tradingQuotationService";

const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

const unwrapList = (response) => {
  const data = unwrap(response);
  return Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : [];
};

const numberValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const amount = (value, currency = "INR") => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numberValue(value));
  } catch {
    return numberValue(value).toFixed(2);
  }
};

const dateText = (value) => {
  if (!value) return "—";
  const text = String(value).slice(0, 10);
  const parts = text.split("-");
  if (parts.length !== 3) return text;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const display = (value) =>
  value === null || value === undefined || value === "" ? "—" : value;

const statusColors = {
  DRAFT: { background: "#f0efff", color: "#6254d9" },
  PENDING_APPROVAL: { background: "#fff4df", color: "#9b6500" },
  APPROVED: { background: "#e8f7ee", color: "#247947" },
  REJECTED: { background: "#fff0ef", color: "#b42318" },
  ACCEPTED: { background: "#e8f7ee", color: "#247947" },
  CLOSED: { background: "#edf0f5", color: "#596176" },
  SENT_TO_CUSTOMER: {
    background: "#e8f2ff",
    color: "#2459a9",
  },
  APPROVED_BY_CUSTOMER: {
    background: "#e8f7ee",
    color: "#247947",
  },
  REJECTED_BY_CUSTOMER: {
    background: "#fff0ef",
    color: "#b42318",
  },
};

const styles = {
  page: {
    minHeight: "100vh",
    padding: "24px",
    background: "#f7f8fc",
    color: "#20243a",
    fontFamily: "Inter, Arial, sans-serif",
  },
  container: { maxWidth: "1440px", margin: "0 auto" },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "22px",
  },
  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: 750,
    letterSpacing: "-0.5px",
  },
  subtitle: { margin: "7px 0 0", color: "#777e94", fontSize: "13px" },
  actions: { display: "flex", flexWrap: "wrap", gap: "9px" },
  button: {
    height: "40px",
    padding: "0 15px",
    borderRadius: "8px",
    border: "1px solid #dedfec",
    background: "#fff",
    color: "#4e536b",
    fontSize: "12px",
    fontWeight: 700,
    cursor: "pointer",
  },
  primary: {
    height: "40px",
    padding: "0 17px",
    borderRadius: "8px",
    border: "none",
    background: "#6254d9",
    color: "#fff",
    fontSize: "12px",
    fontWeight: 750,
    cursor: "pointer",
  },
  disabled: { opacity: 0.55, cursor: "not-allowed" },
  card: {
    background: "#fff",
    border: "1px solid #e9eaf2",
    borderRadius: "14px",
    padding: "22px",
    marginBottom: "20px",
    boxShadow: "0 3px 12px rgba(32,36,58,0.025)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "18px",
  },
  sectionTitle: { margin: 0, fontSize: "16px", fontWeight: 750 },
  sectionSub: { margin: "5px 0 0", color: "#858ba0", fontSize: "12px" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "18px",
  },
  fieldLabel: {
    fontSize: "11px",
    color: "#858ba0",
    marginBottom: "7px",
  },
  fieldValue: {
    fontSize: "13px",
    color: "#30354d",
    fontWeight: 650,
    overflowWrap: "anywhere",
  },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "720px" },
  th: {
    padding: "12px 13px",
    textAlign: "left",
    background: "#f7f8fc",
    color: "#737991",
    fontSize: "11px",
    fontWeight: 750,
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "13px",
    fontSize: "12px",
    color: "#454b63",
    borderBottom: "1px solid #eff0f5",
    verticalAlign: "top",
  },
  right: { textAlign: "right", whiteSpace: "nowrap" },
  badge: {
    display: "inline-block",
    padding: "5px 10px",
    borderRadius: "20px",
    fontSize: "10px",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(175px, 1fr))",
    gap: "12px",
  },
  summaryBox: {
    background: "#f8f9fd",
    border: "1px solid #eceef5",
    borderRadius: "10px",
    padding: "15px",
  },
  summaryLabel: { color: "#7b8298", fontSize: "11px", marginBottom: "8px" },
  summaryValue: {
    color: "#292d43",
    fontSize: "18px",
    fontWeight: 750,
    overflowWrap: "anywhere",
  },
  empty: {
    padding: "26px 12px",
    textAlign: "center",
    color: "#858ba0",
    fontSize: "12px",
  },
  alert: {
    background: "#fff0ef",
    border: "1px solid #ffd9d5",
    color: "#b42318",
    borderRadius: "9px",
    padding: "12px 14px",
    marginBottom: "18px",
    fontSize: "13px",
  },
  info: {
    background: "#f1f0ff",
    border: "1px solid #e0ddff",
    color: "#5549bb",
    borderRadius: "9px",
    padding: "12px 14px",
    marginBottom: "18px",
    fontSize: "12px",
    lineHeight: 1.65,
  },
  textInput: {
    boxSizing: "border-box",
    width: "100%",
    minHeight: "40px",
    border: "1px solid #dfe2ed",
    borderRadius: "8px",
    padding: "9px 11px",
    font: "inherit",
    fontSize: "12px",
    outlineColor: "#6254d9",
  },
  textarea: {
    boxSizing: "border-box",
    width: "100%",
    minHeight: "78px",
    border: "1px solid #dfe2ed",
    borderRadius: "8px",
    padding: "10px 11px",
    font: "inherit",
    fontSize: "12px",
    resize: "vertical",
    outlineColor: "#6254d9",
  },
  timelineItem: {
    borderLeft: "2px solid #e4e1ff",
    padding: "2px 0 16px 15px",
    marginLeft: "5px",
  },
  smallText: { fontSize: "11px", color: "#858ba0", marginTop: "5px" },
};

function Section({ title, subtitle, children, right }) {
  return (
    <section style={styles.card}>
      <div style={styles.sectionHeader}>
        <div>
          <h2 style={styles.sectionTitle}>{title}</h2>
          {subtitle ? <p style={styles.sectionSub}>{subtitle}</p> : null}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function StatusBadge({ status }) {
  const code = String(status || "UNKNOWN").toUpperCase();
  const color = statusColors[code] || {
    background: "#edf0f5",
    color: "#596176",
  };

  return (
    <span style={{ ...styles.badge, ...color }}>
      {code.replaceAll("_", " ")}
    </span>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <div style={styles.fieldLabel}>{label}</div>
      <div style={styles.fieldValue}>{display(value)}</div>
    </div>
  );
}

export default function TradingQuotationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [quotation, setQuotation] = useState(null);
  const [terms, setTerms] = useState([]);
  const [activeMasterTerms, setActiveMasterTerms] = useState([]);
  const [selectedTermIds, setSelectedTermIds] = useState([]);
  const [termValues, setTermValues] = useState({});
  const [termsLoading, setTermsLoading] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState(null);

  const [revisionReason, setRevisionReason] = useState("");
  const [showRevisionForm, setShowRevisionForm] = useState(false);

  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [showApprovalForm, setShowApprovalForm] = useState(false);

  const [showCustomerDecisionForm, setShowCustomerDecisionForm] =
    useState(false);
  const [customerDecision, setCustomerDecision] = useState("ACCEPTED");
  const [decisionReference, setDecisionReference] = useState("");
  const [customerRejectionReason, setCustomerRejectionReason] = useState("");

  const loadQuotation = useCallback(async () => {
    setLoading(true);
    setError("");
    setNotice("");
    setTermsLoading(true);

    try {
      const response = await tradingQuotationService.getQuotation(id);
      const data = unwrap(response);

      if (!data?.id) {
        throw new Error("Quotation details were not returned by the server.");
      }

      setQuotation(data);

      const optionalRequests = await Promise.allSettled([
        tradingQuotationService.getActiveQuotationTerms(),
        tradingQuotationService.getQuotationTerms(id),
        tradingQuotationService.getAuditHistory(id),
        tradingQuotationService.getCommunications(id),
      ]);

      const [
        masterTermsResult,
        termsResult,
        auditResult,
        communicationResult,
      ] = optionalRequests;

      
      
      const masterTerms =
        masterTermsResult.status === "fulfilled"
          ? unwrapList(masterTermsResult.value)
          : [];

      setActiveMasterTerms(masterTerms);

      if (termsResult.status === "fulfilled") {
        const savedTerms = unwrapList(termsResult.value);
        setTerms(savedTerms);

        if (savedTerms.length > 0) {
          // Preserve the quotation's saved selection and edited values.
          setSelectedTermIds(
            savedTerms
              .map((term) => Number(term.term_master_id))
              .filter(Number.isFinite)
          );

          setTermValues(
            Object.fromEntries(
              savedTerms.map((term) => [
                Number(term.term_master_id),
                term.value ?? "",
              ])
            )
          );
        } else {
          // No saved terms: select all active terms and load their templates.
          setSelectedTermIds(
            masterTerms
              .map((term) => Number(term.id))
              .filter(Number.isFinite)
          );

          setTermValues(
            Object.fromEntries(
              masterTerms.map((term) => [
                Number(term.id),
                term.template_text ?? "",
              ])
            )
          );
        }
      } else {
        // If saved terms cannot be fetched, initialize from the master.
        setTerms([]);

        setSelectedTermIds(
          masterTerms
            .map((term) => Number(term.id))
            .filter(Number.isFinite)
        );

        setTermValues(
          Object.fromEntries(
            masterTerms.map((term) => [
              Number(term.id),
              term.template_text ?? "",
            ])
          )
        );
      }


      if (auditResult.status === "fulfilled") {
        setAuditHistory(unwrapList(auditResult.value));
      } else {
        setAuditHistory([]);
      }

      if (communicationResult.status === "fulfilled") {
        setCommunications(unwrapList(communicationResult.value));
      } else {
        setCommunications([]);
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load the quotation."
      );
      setQuotation(null);
    } finally {
      setLoading(false);
      setTermsLoading(false);
    }
  }, [id]);

  const handleToggleQuotationTerm = (termId) => {
    const numericId = Number(termId);

    const masterTerm = activeMasterTerms.find(
      (term) => Number(term.id) === numericId
    );

    if (!masterTerm || !canEdit) return;

    const isSelected = selectedTermIds.includes(numericId);

    if (isSelected && masterTerm.is_mandatory) {
      setError("This is a mandatory term and cannot be removed.");
      return;
    }

    setError("");

    setSelectedTermIds((previous) =>
      isSelected
        ? previous.filter((selectedId) => selectedId !== numericId)
        : [...previous, numericId]
    );
  };

  const handleQuotationTermValueChange = (termId, value) => {
    const numericId = Number(termId);

    setTermValues((previous) => ({
      ...previous,
      [numericId]: value,
    }));

    setError("");
  };

  const handleSaveQuotationTerms = async () => {
    if (!canEdit) {
      setError("Terms can only be changed for the current draft quotation.");
      return;
    }

    const mandatoryNotSelected = activeMasterTerms.find(
      (term) =>
        term.is_mandatory &&
        !selectedTermIds.includes(Number(term.id))
    );

    if (mandatoryNotSelected) {
      setError(
        `Please select the mandatory term: ${mandatoryNotSelected.term_name}.`
      );
      return;
    }

    const selectedTerms = activeMasterTerms.filter((term) =>
      selectedTermIds.includes(Number(term.id))
    );

    const missingMandatoryValue = selectedTerms.find(
      (term) =>
        term.is_mandatory &&
        !String(termValues[Number(term.id)] ?? "").trim()
    );

    if (missingMandatoryValue) {
      setError(
        `Please enter a value for mandatory term: ${missingMandatoryValue.term_name}.`
      );
      return;
    }

    const payload = selectedTermIds.map((termId) => ({
      term_master_id: Number(termId),
      value: termValues[Number(termId)] ?? "",
    }));

    setSaving(true);
    setError("");
    setNotice("");

    try {
      const response = await tradingQuotationService.saveQuotationTerms(
        id,
        payload
      );

      const savedTerms = unwrapList(response);

      setTerms(savedTerms);
      setSelectedTermIds(
        savedTerms
          .map((term) => Number(term.term_master_id))
          .filter(Number.isFinite)
      );

      setTermValues(
        Object.fromEntries(
          savedTerms.map((term) => [
            Number(term.term_master_id),
            term.value ?? "",
          ])
        )
      );

      setNotice("Quotation terms and conditions saved successfully.");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to save quotation terms."
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadQuotation();
  }, [loadQuotation]);

  const items = Array.isArray(quotation?.items) ? quotation.items : [];
  const charges = Array.isArray(quotation?.charges) ? quotation.charges : [];
  const currency = quotation?.currency_code || "INR";

  const customerCharges = useMemo(
    () =>
      charges.filter(
        (charge) =>
          String(charge?.payer_type || "").toUpperCase() === "CUSTOMER"
      ),
    [charges]
  );

  const excludedCharges = useMemo(
    () =>
      charges.filter(
        (charge) =>
          String(charge?.payer_type || "").toUpperCase() !== "CUSTOMER"
      ),
    [charges]
  );

  const currentStatus = String(quotation?.status_code || "").toUpperCase();

  const isCurrentVersion =
    quotation?.is_current_version === true ||
    quotation?.is_current_version === 1;

  const canSendToCustomer =
    currentStatus === "APPROVED" && isCurrentVersion;

  const canRecordCustomerDecision =
    currentStatus === "SENT_TO_CUSTOMER" && isCurrentVersion;

  const canSubmit = currentStatus === "DRAFT" && isCurrentVersion;
  const canEdit = canSubmit;

  const handleDownloadPdf = async () => {
    setError("");
    setNotice("");
    setDownloadingPdf(true);

    try {
      const response =
        await tradingQuotationService.downloadQuotationPdf(id);

      const blob =
        response?.data instanceof Blob
          ? response.data
          : new Blob([response?.data], {
              type: "application/pdf",
            });

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = `${
        quotation?.quotation_no || `quotation-${id}`
      }.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(objectUrl);

      setNotice("Quotation PDF download started.");
    } catch (err) {
      let message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to download the quotation PDF.";

      if (err?.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const errorData = JSON.parse(errorText);
          message = errorData?.message || message;
        } catch {
          // Keep the fallback error message.
        }
      }

      setError(message);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const startEditing = () => {
    setError("");
    setNotice("");

    setEditDraft({
      subject: quotation.subject || "",
      customer_reference: quotation.customer_reference || "",
      remarks: quotation.remarks || "",
      valid_until: quotation.valid_until
        ? String(quotation.valid_until).slice(0, 10)
        : "",
      items: items.map((item) => ({
        id: item.id,
        quantity: String(item.quantity ?? ""),
        unit_selling_price: String(item.unit_selling_price ?? ""),
        discount_percent: String(item.discount_percent ?? "0"),
      })),
      charges: charges.map((charge) => ({
        id: charge.id,
        charge_type: charge.charge_type || "",
        payer_type: String(charge.payer_type || "COMPANY").toUpperCase(),
        customer_id: charge.customer_id ?? null,
        charge_amount: String(charge.charge_amount ?? "0"),
        remarks: charge.remarks || "",
      })),
    });

    setEditMode(true);
  };

  const updateEditField = (field, value) => {
    setEditDraft((current) => ({ ...current, [field]: value }));
  };

  const updateEditItem = (index, field, value) => {
    setEditDraft((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const updateEditCharge = (index, field, value) => {
    setEditDraft((current) => ({
      ...current,
      charges: current.charges.map((charge, chargeIndex) =>
        chargeIndex === index ? { ...charge, [field]: value } : charge
      ),
    }));
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();
    if (!editDraft) return;

    setSaving(true);
    setError("");
    setNotice("");

    try {
      const payload = {
        subject: editDraft.subject,
        customer_reference: editDraft.customer_reference,
        remarks: editDraft.remarks,
        valid_until: editDraft.valid_until || null,
        items: editDraft.items.map((item) => ({
          id: item.id,
          quantity: Number(item.quantity),
          unit_selling_price: Number(item.unit_selling_price),
          discount_percent: Number(item.discount_percent),
        })),
        charges: editDraft.charges.map((charge) => ({
          ...(charge.id ? { id: charge.id } : {}),
          charge_type: charge.charge_type,
          payer_type: charge.payer_type,
          customer_id: charge.customer_id,
          charge_amount: Number(charge.charge_amount),
          remarks: charge.remarks,
        })),
      };

      await tradingQuotationService.updateQuotation(id, payload);

      setEditMode(false);
      setEditDraft(null);
      setNotice("Quotation changes saved successfully.");

      await loadQuotation();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to save quotation changes."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleMarkSentToCustomer = async () => {
    setError("");
    setNotice("");
    setSaving(true);

    try {
      await tradingQuotationService.markSentToCustomer(id, {});
      setNotice("Quotation marked as sent to customer.");
      await loadQuotation();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to mark quotation as sent to customer."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCustomerDecision = async (event) => {
    event.preventDefault();

    const reason = customerRejectionReason.trim();

    if (customerDecision === "REJECTED" && !reason) {
      setError("Please provide a reason for rejecting the quotation.");
      return;
    }

    setError("");
    setNotice("");
    setSaving(true);

    try {
      await tradingQuotationService.recordCustomerDecision(id, {
        decision: customerDecision,
        decision_reference: decisionReference.trim() || null,
        customer_rejection_reason:
          customerDecision === "REJECTED" ? reason : null,
      });

      setShowCustomerDecisionForm(false);
      setCustomerRejectionReason("");
      setDecisionReference("");

      setNotice(
        customerDecision === "ACCEPTED"
          ? "Customer acceptance recorded successfully."
          : "Customer rejection recorded successfully."
      );

      await loadQuotation();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to record customer decision."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitApproval = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    setSaving(true);

    try {
      await tradingQuotationService.submitForApproval(id, {
        remarks: approvalRemarks.trim(),
      });

      setNotice("Quotation submitted for approval.");
      setShowApprovalForm(false);
      await loadQuotation();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to submit the quotation for approval."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRevision = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");

    if (!revisionReason.trim()) {
      setError("Please provide a reason for the revision.");
      return;
    }

    setSaving(true);

    try {
      const response = await tradingQuotationService.createRevision(
        id,
        revisionReason.trim()
      );

      const revised = unwrap(response);
      const revisedId = revised?.id ?? revised?.quotation?.id;

      if (!revisedId) {
        throw new Error("The revision was created without a returned ID.");
      }

      navigate(`/trading/quotations/${revisedId}`);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to create a quotation revision."
      );
    } finally {
      setSaving(false);
    }
  };
    if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.card}>Loading quotation details…</div>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.topBar}>
            <h1 style={styles.title}>Trading Quotation</h1>
            <button
              style={styles.button}
              onClick={() => navigate("/trading/quotations")}
            >
              Back to Quotations
            </button>
          </div>
          <div style={styles.alert}>{error || "Quotation not found."}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.topBar}>
          <div>
            <h1 style={styles.title}>
              {quotation.quotation_no || `Quotation #${quotation.id}`}
            </h1>
            <p style={styles.subtitle}>
              Trading Quotation · Version {display(quotation.version_no)}
              {" · "}
              {isCurrentVersion ? "Current Version" : "Previous Version"}
            </p>
            <div style={{ marginTop: "12px" }}>
              <StatusBadge status={quotation.status_code} />
            </div>
          </div>

          <div style={styles.actions}>
            <button
              style={styles.button}
              onClick={() => navigate("/trading/quotations")}
            >
              ← Back
            </button>

            <button
              type="button"
              style={{
                ...styles.button,
                ...(downloadingPdf ? styles.disabled : {}),
              }}
              disabled={downloadingPdf || loading}
              onClick={handleDownloadPdf}
            >
              {downloadingPdf ? "Downloading PDF…" : "Download PDF"}
            </button>

            {canSendToCustomer ? (
              <button
                type="button"
                style={{
                  ...styles.primary,
                  ...(saving ? styles.disabled : {}),
                }}
                disabled={saving}
                onClick={handleMarkSentToCustomer}
              >
                {saving ? "Processing..." : "Send to Customer"}
              </button>
            ) : null}

            {canRecordCustomerDecision ? (
              <>
                <button
                  type="button"
                  style={styles.primary}
                  disabled={saving}
                  onClick={() => {
                    setCustomerDecision("ACCEPTED");
                    setShowCustomerDecisionForm(true);
                    setError("");
                  }}
                >
                  Accept
                </button>

                <button
                  type="button"
                  style={styles.button}
                  disabled={saving}
                  onClick={() => {
                    setCustomerDecision("REJECTED");
                    setShowCustomerDecisionForm(true);
                    setError("");
                  }}
                >
                  Reject
                </button>
              </>
            ) : null}

            {canEdit && !editMode ? (
              <button
                style={styles.primary}
                disabled={saving}
                onClick={startEditing}
              >
                Edit Quotation
              </button>
            ) : null}

            {editMode ? (
              <>
                <button
                  style={styles.primary}
                  disabled={saving}
                  onClick={handleSaveEdit}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
                <button
                  style={styles.button}
                  disabled={saving}
                  onClick={() => {
                    setEditMode(false);
                    setEditDraft(null);
                  }}
                >
                  Cancel Edit
                </button>
              </>
            ) : null}

            <button
              style={{
                ...styles.primary,
                ...(canSubmit && !editMode ? {} : styles.disabled),
              }}
              disabled={!canSubmit || saving || editMode}
              onClick={() => setShowApprovalForm((value) => !value)}
            >
              Submit for Approval
            </button>

            <button
              style={styles.button}
              disabled={saving || !isCurrentVersion}
              onClick={() => setShowRevisionForm((value) => !value)}
            >
              Create Revision
            </button>
          </div>
        </div>

        {error ? (
          <div role="alert" style={styles.alert}>
            {error}
          </div>
        ) : null}

        {notice ? (
          <div
            style={{
              ...styles.info,
              background: "#eaf8ef",
              borderColor: "#d3efdd",
              color: "#247947",
            }}
          >
            {notice}
          </div>
        ) : null}

        {!isCurrentVersion ? (
          <div style={styles.info}>
            This is a previous quotation version. Approval submission is
            available only for the current version.
          </div>
        ) : null}

        {showApprovalForm && canSubmit ? (
          <Section
            title="Submit for Approval"
            subtitle="Submit this quotation through the configured approval workflow"
          >
            <form onSubmit={handleSubmitApproval}>
              <div style={{ marginBottom: "14px" }}>
                <div style={styles.fieldLabel}>Remarks</div>
                <textarea
                  style={styles.textarea}
                  value={approvalRemarks}
                  onChange={(event) => setApprovalRemarks(event.target.value)}
                  placeholder="Optional remarks for the approver"
                />
              </div>

              <div style={styles.actions}>
                <button
                  type="button"
                  style={styles.button}
                  disabled={saving}
                  onClick={() => setShowApprovalForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    ...styles.primary,
                    ...(saving ? styles.disabled : {}),
                  }}
                  disabled={saving}
                >
                  {saving ? "Submitting..." : "Confirm Submission"}
                </button>
              </div>
            </form>
          </Section>
        ) : null}

        {showRevisionForm && isCurrentVersion ? (
          <Section
            title="Create Quotation Revision"
            subtitle="Create a new version while retaining the existing quotation history"
          >
            <form onSubmit={handleCreateRevision}>
              <div style={{ marginBottom: "14px" }}>
                <div style={styles.fieldLabel}>Revision Reason *</div>
                <textarea
                  style={styles.textarea}
                  value={revisionReason}
                  onChange={(event) => setRevisionReason(event.target.value)}
                  required
                  placeholder="Enter the reason for this revision"
                />
              </div>

              <div style={styles.actions}>
                <button
                  type="button"
                  style={styles.button}
                  disabled={saving}
                  onClick={() => setShowRevisionForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    ...styles.primary,
                    ...(saving ? styles.disabled : {}),
                  }}
                  disabled={saving}
                >
                  {saving ? "Creating..." : "Create Revision"}
                </button>
              </div>
            </form>
          </Section>
        ) : null}

        {showCustomerDecisionForm && canRecordCustomerDecision ? (
          <Section
            title="Record Customer Decision"
            subtitle="Record the customer's acceptance or rejection of this quotation"
          >
            <form onSubmit={handleCustomerDecision}>
              <div style={{ marginBottom: "14px" }}>
                <div style={styles.fieldLabel}>Decision</div>
                <select
                  style={styles.textInput}
                  value={customerDecision}
                  onChange={(event) =>
                    setCustomerDecision(event.target.value)
                  }
                >
                  <option value="ACCEPTED">Accepted</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>

              <div style={{ marginBottom: "14px" }}>
                <div style={styles.fieldLabel}>Decision Reference</div>
                <input
                  style={styles.textInput}
                  value={decisionReference}
                  onChange={(event) => setDecisionReference(event.target.value)}
                  placeholder="Optional reference number or details"
                />
              </div>

              {customerDecision === "REJECTED" ? (
                <div style={{ marginBottom: "14px" }}>
                  <div style={styles.fieldLabel}>Rejection Reason *</div>
                  <textarea
                    style={styles.textarea}
                    value={customerRejectionReason}
                    onChange={(event) =>
                      setCustomerRejectionReason(event.target.value)
                    }
                    required
                    placeholder="Enter the reason for rejection"
                  />
                </div>
              ) : null}

              <div style={{ ...styles.actions, marginTop: "14px" }}>
                <button
                  type="button"
                  style={styles.button}
                  disabled={saving}
                  onClick={() => setShowCustomerDecisionForm(false)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  style={{
                    ...styles.primary,
                    ...(saving ? styles.disabled : {}),
                  }}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Confirm Decision"}
                </button>
              </div>
            </form>
          </Section>
        ) : null}

        <Section
          title="Quotation Details"
          subtitle="Header and source information"
        >
          <div style={styles.grid}>
            <DetailField
              label="Quotation Number"
              value={quotation.quotation_no}
            />
            <DetailField label="Version" value={quotation.version_no} />
            <DetailField
              label="Quotation Date"
              value={dateText(quotation.quotation_date)}
            />

            <div>
              <div style={styles.fieldLabel}>Valid Until</div>
              {editMode ? (
                <input
                  type="date"
                  style={styles.textInput}
                  value={editDraft?.valid_until || ""}
                  onChange={(event) =>
                    updateEditField("valid_until", event.target.value)
                  }
                />
              ) : (
                <div style={styles.fieldValue}>
                  {dateText(quotation.valid_until)}
                </div>
              )}
            </div>

            <DetailField
              label="Customer"
              value={
                quotation.customer_name
                  ? `${quotation.customer_name} (${quotation.customer_code || quotation.business_partner_id})`
                  : quotation.business_partner_id
              }
            />
            <DetailField
              label="Trading Enquiry"
              value={
                quotation.enquiry_no
                  ? `${quotation.enquiry_no} (ID: ${quotation.enquiry_id})`
                  : quotation.enquiry_id
              }
            />
            <DetailField
              label="Source Costing"
              value={
                quotation.costing_no
                  ? `${quotation.costing_no} (ID: ${quotation.costing_id})`
                  : quotation.costing_id
              }
            />
            <DetailField label="Currency" value={quotation.currency_code} />

            <div>
              <div style={styles.fieldLabel}>Customer Reference</div>
              {editMode ? (
                <input
                  style={styles.textInput}
                  value={editDraft?.customer_reference || ""}
                  onChange={(event) =>
                    updateEditField("customer_reference", event.target.value)
                  }
                />
              ) : (
                <div style={styles.fieldValue}>
                  {display(quotation.customer_reference)}
                </div>
              )}
            </div>

            <div>
              <div style={styles.fieldLabel}>Subject</div>
              {editMode ? (
                <input
                  style={styles.textInput}
                  value={editDraft?.subject || ""}
                  onChange={(event) =>
                    updateEditField("subject", event.target.value)
                  }
                />
              ) : (
                <div style={styles.fieldValue}>
                  {display(quotation.subject)}
                </div>
              )}
            </div>

            <DetailField
              label="Customer Decision"
              value={quotation.customer_decision}
            />
            <DetailField
              label="Decision Reference"
              value={quotation.customer_decision_reference}
            />
            <DetailField
              label="Customer Rejection Reason"
              value={quotation.customer_rejection_reason}
            />
            <DetailField label="Created At" value={quotation.created_at} />
            <DetailField label="Updated At" value={quotation.updated_at} />
            <DetailField
              label="Revision Reason"
              value={quotation.revision_reason}
            />
          </div>

          {editMode || quotation.remarks ? (
            <div style={{ marginTop: "20px" }}>
              <div style={styles.fieldLabel}>Remarks</div>
              {editMode ? (
                <textarea
                  style={styles.textarea}
                  value={editDraft?.remarks || ""}
                  onChange={(event) =>
                    updateEditField("remarks", event.target.value)
                  }
                />
              ) : (
                <div
                  style={{
                    ...styles.fieldValue,
                    fontWeight: 400,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {quotation.remarks}
                </div>
              )}
            </div>
          ) : null}
        </Section>
                <Section
          title="Items & Pricing"
          subtitle="Persisted quotation line values"
        >
          {items.length === 0 ? (
            <div style={styles.empty}>No quotation items were returned.</div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Description</th>
                    <th style={{ ...styles.th, ...styles.right }}>Quantity</th>
                    <th style={{ ...styles.th, ...styles.right }}>Unit Price</th>
                    <th style={{ ...styles.th, ...styles.right }}>Gross</th>
                    <th style={{ ...styles.th, ...styles.right }}>Discount</th>
                    <th style={{ ...styles.th, ...styles.right }}>Taxable</th>
                    <th style={{ ...styles.th, ...styles.right }}>Tax</th>
                    <th style={{ ...styles.th, ...styles.right }}>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={item.id ?? index}>
                      <td style={styles.td}>
                        {item.item_seq_no ?? index + 1}
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontWeight: 700 }}>
                          {display(item.item_description)}
                        </div>
                        <div style={styles.smallText}>
                          Raw Material ID: {display(item.raw_material_id)}
                        </div>
                      </td>
                      <td style={{ ...styles.td, ...styles.right }}>
                        {editMode ? (
                          <input
                            type="number"
                            min="0.000001"
                            step="any"
                            style={{ ...styles.textInput, minWidth: "95px" }}
                            value={
                              editDraft?.items[index]?.quantity ??
                              item.quantity
                            }
                            onChange={(event) =>
                              updateEditItem(
                                index,
                                "quantity",
                                event.target.value
                              )
                            }
                          />
                        ) : (
                          numberValue(item.quantity).toLocaleString("en-IN")
                        )}
                      </td>
                      <td style={{ ...styles.td, ...styles.right }}>
                        {editMode ? (
                          <input
                            type="number"
                            min="0"
                            step="any"
                            style={{ ...styles.textInput, minWidth: "110px" }}
                            value={
                              editDraft?.items[index]?.unit_selling_price ??
                              item.unit_selling_price
                            }
                            onChange={(event) =>
                              updateEditItem(
                                index,
                                "unit_selling_price",
                                event.target.value
                              )
                            }
                          />
                        ) : (
                          amount(item.unit_selling_price, currency)
                        )}
                      </td>
                      <td style={{ ...styles.td, ...styles.right }}>
                        {amount(item.line_gross_amount, currency)}
                      </td>
                      <td style={{ ...styles.td, ...styles.right }}>
                        {editMode ? (
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="any"
                            style={{ ...styles.textInput, minWidth: "90px" }}
                            value={
                              editDraft?.items[index]?.discount_percent ??
                              item.discount_percent
                            }
                            onChange={(event) =>
                              updateEditItem(
                                index,
                                "discount_percent",
                                event.target.value
                              )
                            }
                          />
                        ) : (
                          amount(item.discount_amount, currency)
                        )}
                        {!editMode ? (
                          <div style={styles.smallText}>
                            {numberValue(item.discount_percent).toFixed(2)}%
                          </div>
                        ) : (
                          <div style={styles.smallText}>Discount %</div>
                        )}
                      </td>
                      <td style={{ ...styles.td, ...styles.right }}>
                        {amount(item.taxable_amount, currency)}
                      </td>
                      <td style={{ ...styles.td, ...styles.right }}>
                        {amount(item.tax_amount, currency)}
                      </td>
                      <td
                        style={{
                          ...styles.td,
                          ...styles.right,
                          fontWeight: 750,
                        }}
                      >
                        {amount(item.line_total_amount, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <Section
          title="Additional Charges"
          subtitle="Customer charges and excluded charges are shown separately."
        >
          {(editMode ? editDraft?.charges || [] : charges).length === 0 ? (
            <div style={styles.empty}>No additional charges were returned.</div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Charge Type</th>
                    <th style={styles.th}>Payer</th>
                    <th style={styles.th}>Remarks</th>
                    <th style={{ ...styles.th, ...styles.right }}>Amount</th>
                    <th style={{ ...styles.th, ...styles.right }}>GST Rate</th>
                    <th style={{ ...styles.th, ...styles.right }}>GST</th>
                    <th style={styles.th}>Treatment</th>
                    {editMode ? <th style={styles.th}>Action</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {(editMode ? editDraft?.charges || [] : charges).map(
                    (charge, index) => {
                      const savedCharge = charge.id
                        ? charges.find((saved) => saved.id === charge.id)
                        : null;

                      const included =
                        String(charge.payer_type || "").toUpperCase() ===
                        "CUSTOMER";

                      return (
                        <tr key={charge.id ?? index}>
                          <td style={styles.td}>
                            {editMode ? (
                              <input
                                style={styles.textInput}
                                value={
                                  editDraft?.charges[index]?.charge_type ??
                                  charge.charge_type ??
                                  ""
                                }
                                onChange={(event) =>
                                  updateEditCharge(
                                    index,
                                    "charge_type",
                                    event.target.value
                                  )
                                }
                              />
                            ) : (
                              display(charge.charge_type)
                            )}
                          </td>
                          <td style={styles.td}>
                            {editMode ? (
                              <select
                                style={styles.textInput}
                                value={
                                  editDraft?.charges[index]?.payer_type ??
                                  charge.payer_type
                                }
                                onChange={(event) =>
                                  updateEditCharge(
                                    index,
                                    "payer_type",
                                    event.target.value
                                  )
                                }
                              >
                                <option value="CUSTOMER">CUSTOMER</option>
                                <option value="COMPANY">COMPANY</option>
                                <option value="VENDOR">VENDOR</option>
                              </select>
                            ) : (
                              display(charge.payer_type)
                            )}
                          </td>
                          <td style={styles.td}>
                            {editMode ? (
                              <input
                                style={styles.textInput}
                                value={
                                  editDraft?.charges[index]?.remarks ??
                                  charge.remarks ??
                                  ""
                                }
                                onChange={(event) =>
                                  updateEditCharge(
                                    index,
                                    "remarks",
                                    event.target.value
                                  )
                                }
                              />
                            ) : (
                              display(charge.remarks)
                            )}
                          </td>
                          <td style={{ ...styles.td, ...styles.right }}>
                            {editMode ? (
                              <input
                                type="number"
                                min="0"
                                step="any"
                                style={{ ...styles.textInput, minWidth: "100px" }}
                                value={
                                  editDraft?.charges[index]?.charge_amount ??
                                  charge.charge_amount
                                }
                                onChange={(event) =>
                                  updateEditCharge(
                                    index,
                                    "charge_amount",
                                    event.target.value
                                  )
                                }
                              />
                            ) : (
                              amount(charge.charge_amount, currency)
                            )}
                          </td>
                          <td style={{ ...styles.td, ...styles.right }}>
                            {savedCharge
                              ? `${numberValue(savedCharge.gst_rate).toFixed(2)}%`
                              : editMode
                                ? "Resolved on save"
                                : `${numberValue(charge.gst_rate).toFixed(2)}%`}
                          </td>
                          <td style={{ ...styles.td, ...styles.right }}>
                            {savedCharge
                              ? amount(savedCharge.gst_amount, currency)
                              : editMode
                                ? "Resolved on save"
                                : amount(charge.gst_amount, currency)}
                          </td>
                          <td style={styles.td}>
                            {included ? (
                              <StatusBadge status="ACCEPTED" />
                            ) : (
                              <span style={styles.badge}>Excluded</span>
                            )}
                          </td>
                          {editMode ? (
                            <td style={styles.td}>
                              <button
                                type="button"
                                style={styles.button}
                                onClick={() =>
                                  setEditDraft((current) => ({
                                    ...current,
                                    charges: current.charges.filter(
                                      (_, chargeIndex) =>
                                        chargeIndex !== index
                                    ),
                                  }))
                                }
                              >
                                Remove
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          )}

          {editMode ? (
            <div style={{ marginTop: "12px" }}>
              <button
                type="button"
                style={styles.button}
                onClick={() =>
                  setEditDraft((current) => ({
                    ...current,
                    charges: [
                      ...current.charges,
                      {
                        charge_type: "",
                        payer_type: "COMPANY",
                        customer_id: null,
                        charge_amount: "0",
                        remarks: "",
                      },
                    ],
                  }))
                }
              >
                + Add Charge
              </button>
              <div style={styles.smallText}>
                GST rate is resolved from the existing charge tax
                configuration. It cannot be entered here.
              </div>
            </div>
          ) : null}

          <div style={{ ...styles.summaryGrid, marginTop: "16px" }}>
            <div style={styles.summaryBox}>
              <div style={styles.summaryLabel}>Customer-payable charges</div>
              <div style={styles.summaryValue}>
                {amount(
                  customerCharges.reduce(
                    (total, charge) =>
                      total + numberValue(charge.charge_amount),
                    0
                  ),
                  currency
                )}
              </div>
            </div>
            <div style={styles.summaryBox}>
              <div style={styles.summaryLabel}>Excluded charges</div>
              <div style={styles.summaryValue}>
                {amount(
                  excludedCharges.reduce(
                    (total, charge) =>
                      total + numberValue(charge.charge_amount),
                    0
                  ),
                  currency
                )}
              </div>
            </div>
          </div>
        </Section>

        <Section
          title="Commercial Summary"
          subtitle="Totals returned by the quotation backend"
        >
          <div style={styles.summaryGrid}>
            <div style={styles.summaryBox}>
              <div style={styles.summaryLabel}>Item Gross</div>
              <div style={styles.summaryValue}>
                {amount(quotation.item_gross_amount, currency)}
              </div>
            </div>
            <div style={styles.summaryBox}>
              <div style={styles.summaryLabel}>Item Discount</div>
              <div style={styles.summaryValue}>
                {amount(quotation.item_discount_amount, currency)}
              </div>
            </div>
            <div style={styles.summaryBox}>
              <div style={styles.summaryLabel}>Item Taxable</div>
              <div style={styles.summaryValue}>
                {amount(quotation.item_taxable_amount, currency)}
              </div>
            </div>
            <div style={styles.summaryBox}>
              <div style={styles.summaryLabel}>Total Tax</div>
              <div style={styles.summaryValue}>
                {amount(quotation.total_tax_amount, currency)}
              </div>
            </div>
            <div style={styles.summaryBox}>
              <div style={styles.summaryLabel}>All Charges (Traceability)</div>
              <div style={styles.summaryValue}>
                {amount(quotation.charge_amount, currency)}
              </div>
            </div>
            <div style={styles.summaryBox}>
              <div style={styles.summaryLabel}>Customer Quotation Total</div>
              <div style={{ ...styles.summaryValue, color: "#6254d9" }}>
                {amount(quotation.net_amount, currency)}
              </div>
            </div>
          </div>
        </Section>

        <Section
          title="Terms & Conditions"
          subtitle="Select the applicable master terms, enter values, and save them against this quotation"
          right={
            <button
              type="button"
              style={{
                ...styles.primary,
                ...(!canEdit || saving || termsLoading ? styles.disabled : {}),
              }}
              disabled={!canEdit || saving || termsLoading}
              onClick={handleSaveQuotationTerms}
            >
              {saving ? "Saving..." : "Save Terms"}
            </button>
          }
        >
          {termsLoading ? (
            <div style={styles.empty}>Loading quotation terms...</div>
          ) : activeMasterTerms.length === 0 ? (
            <div style={styles.empty}>
              No active quotation terms were returned by the master terms endpoint.
            </div>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {!canEdit ? (
                <div style={styles.info}>
                  Terms can only be changed for the current draft quotation.
                </div>
              ) : null}

              {activeMasterTerms.map((masterTerm) => {
                const termId = Number(masterTerm.id);
                const isSelected = selectedTermIds.includes(termId);
                const value = termValues[termId] ?? "";
                const fieldType = String(
                  masterTerm.field_type || "TEXT"
                ).toUpperCase();

                return (
                  <div
                    key={termId}
                    style={{
                      border: "1px solid #e7e8f1",
                      background: isSelected ? "#fafaff" : "#ffffff",
                      borderRadius: "10px",
                      padding: "14px",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        cursor: canEdit ? "pointer" : "default",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!canEdit}
                        onChange={() => handleToggleQuotationTerm(termId)}
                        style={{ marginTop: "3px" }}
                      />

                      <span style={{ flex: 1 }}>
                        <span
                          style={{
                            display: "block",
                            fontSize: "13px",
                            fontWeight: 750,
                            color: "#30354d",
                          }}
                        >
                          {masterTerm.term_name}
                          {masterTerm.is_mandatory ? (
                            <span style={{ color: "#b42318" }}> *</span>
                          ) : null}
                        </span>

                        <span
                          style={{
                            display: "block",
                            marginTop: "4px",
                            fontSize: "11px",
                            color: "#858ba0",
                          }}
                        >
                          {masterTerm.term_code}
                          {masterTerm.term_group
                            ? ` · ${masterTerm.term_group}`
                            : ""}
                        </span>
                      </span>
                    </label>

                    {isSelected ? (
                      <div
                        style={{
                          marginTop: "13px",
                          marginLeft: "26px",
                          display: "grid",
                          gap: "10px",
                        }}
                      >
                        {fieldType === "DROPDOWN" ||
                        fieldType === "SELECT" ? (
                          <div>
                            <div style={styles.fieldLabel}>Select value</div>
                            <select
                              value={value}
                              disabled={!canEdit}
                              onChange={(event) =>
                                handleQuotationTermValueChange(
                                  termId,
                                  event.target.value
                                )
                              }
                              style={styles.textInput}
                            >
                              <option value="">Select an option</option>
                              {(masterTerm.options || []).map((option) => (
                                <option
                                  key={option.id ?? option.option_code}
                                  value={option.option_code}
                                >
                                  {option.option_label}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : fieldType === "TEXTAREA" ? (
                          <div>
                            <div style={styles.fieldLabel}>Enter value</div>
                            <textarea
                              value={value}
                              disabled={!canEdit}
                              placeholder={
                                masterTerm.placeholder_text || "Enter value"
                              }
                              onChange={(event) =>
                                handleQuotationTermValueChange(
                                  termId,
                                  event.target.value
                                )
                              }
                              style={styles.textarea}
                            />
                          </div>
                        ) : (
                          <div>
                            <div style={styles.fieldLabel}>Enter value</div>
                            <input
                              type="text"
                              value={value}
                              disabled={!canEdit}
                              placeholder={
                                masterTerm.placeholder_text || "Enter value"
                              }
                              onChange={(event) =>
                                handleQuotationTermValueChange(
                                  termId,
                                  event.target.value
                                )
                              }
                              style={styles.textInput}
                            />
                          </div>
                        )}

                        {masterTerm.template_text ? (
                          <div
                            style={{
                              padding: "10px",
                              borderRadius: "7px",
                              background: "#f4f5fa",
                              fontSize: "12px",
                              lineHeight: 1.7,
                              color: "#555b72",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            <div style={styles.fieldLabel}>
                              Term wording preview
                            </div>
                            {masterTerm.template_text.replace(
                              /\{value\}/g,
                              value || "[value]"
                            )}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}

          {terms.length > 0 ? (
            <div style={{ marginTop: "20px" }}>
              <h3 style={{ fontSize: "14px", marginBottom: "10px" }}>
                Saved Terms
              </h3>
              <div style={{ display: "grid", gap: "10px" }}>
                {terms.map((term, index) => (
                  <div
                    key={term.id ?? term.term_master_id ?? index}
                    style={{
                      border: "1px solid #ecebfa",
                      borderRadius: "8px",
                      padding: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 750,
                        marginBottom: "6px",
                      }}
                    >
                      {term.term_name ||
                        term.term_code ||
                        `Term ${index + 1}`}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        lineHeight: 1.7,
                        whiteSpace: "pre-wrap",
                        color: "#555b72",
                      }}
                    >
                      {display(term.generated_text ?? term.value)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </Section>

        <Section
          title="Approval Trail"
          subtitle="Current quotation status and submission information"
        >
          <div style={styles.info}>
            <strong>Current status:</strong>{" "}
            {currentStatus.replaceAll("_", " ")}. The backend submit-for-approval
            action uses the existing Approval Matrix. This section does not
            fabricate individual approver events.
          </div>

          <div style={styles.grid}>
            <DetailField
              label="Current Version"
              value={isCurrentVersion ? "Yes" : "No"}
            />
            <DetailField
              label="Status"
              value={currentStatus.replaceAll("_", " ")}
            />
            <DetailField
              label="Created By (User ID)"
              value={quotation.created_by}
            />
            <DetailField
              label="Updated By (User ID)"
              value={quotation.updated_by}
            />
          </div>
        </Section>

        <Section
          title="Audit Trail"
          subtitle="Quotation audit events recorded by the backend"
        >
          {auditHistory.length === 0 ? (
            <div style={styles.empty}>
              No audit entries were returned. This can mean no events have
              been recorded yet, or the audit request was unavailable.
            </div>
          ) : (
            auditHistory.map((entry, index) => (
              <div key={entry.id ?? index} style={styles.timelineItem}>
                <div style={{ fontWeight: 750, fontSize: "12px" }}>
                  {display(entry.event_type)}
                  {entry.field_name ? ` · ${entry.field_name}` : ""}
                </div>

                <div style={styles.smallText}>
                  {display(entry.performed_at)}
                  {" · "}User ID: {display(entry.performed_by)}
                </div>

                {entry.old_value !== null &&
                entry.old_value !== undefined ? (
                  <div
                    style={{
                      marginTop: "7px",
                      fontSize: "12px",
                      color: "#737991",
                    }}
                  >
                    Old: {String(entry.old_value)}
                  </div>
                ) : null}

                {entry.new_value !== null &&
                entry.new_value !== undefined ? (
                  <div
                    style={{
                      marginTop: "4px",
                      fontSize: "12px",
                      color: "#454b63",
                    }}
                  >
                    New: {String(entry.new_value)}
                  </div>
                ) : null}

                {entry.reason || entry.remarks ? (
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "12px",
                      color: "#737991",
                    }}
                  >
                    {entry.reason || entry.remarks}
                  </div>
                ) : null}
              </div>
            ))
          )}
        </Section>

        <Section
          title="Quotation Revisions"
          subtitle="Current version and revision lineage"
          right={
            <button
              type="button"
              style={styles.button}
              onClick={() => navigate("/trading/quotations")}
            >
              View Quotation List
            </button>
          }
        >
          <div style={styles.grid}>
            <DetailField
              label="Quotation Version"
              value={quotation.version_no}
            />
            <DetailField
              label="Previous Quotation ID"
              value={quotation.previous_quotation_id}
            />
            <DetailField
              label="Current Version"
              value={isCurrentVersion ? "Yes" : "No"}
            />
            <DetailField
              label="Revision Reason"
              value={quotation.revision_reason}
            />
          </div>

          <p style={{ ...styles.sectionSub, marginTop: "14px" }}>
            The current backend exposes revision creation. A complete revision
            history list requires a dedicated read endpoint; this page shows
            the lineage fields available on the quotation itself.
          </p>
        </Section>

        <Section
          title="Customer Communications"
          subtitle="Communication entries recorded against this quotation"
        >
          {communications.length === 0 ? (
            <div style={styles.empty}>
              No communication entries were returned.
            </div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Recipient</th>
                    <th style={styles.th}>Subject</th>
                    <th style={styles.th}>Sent At</th>
                    <th style={styles.th}>Delivery Status</th>
                    <th style={styles.th}>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {communications.map((entry, index) => (
                    <tr key={entry.id ?? index}>
                      <td style={styles.td}>
                        {display(entry.communication_type)}
                      </td>
                      <td style={styles.td}>{display(entry.recipient)}</td>
                      <td style={styles.td}>{display(entry.subject)}</td>
                      <td style={styles.td}>{display(entry.sent_at)}</td>
                      <td style={styles.td}>
                        {display(entry.delivery_status)}
                      </td>
                      <td style={styles.td}>{display(entry.remarks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingBottom: "24px",
          }}
        >
          <button
            type="button"
            style={styles.button}
            onClick={loadQuotation}
            disabled={loading || saving}
          >
            Refresh Details
          </button>
        </div>
      </div>
    </div>
  );
}